import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import type { Session } from '@supabase/supabase-js';
import { motion } from 'motion/react';
import { ArrowRight, Loader2, Store, PartyPopper } from 'lucide-react';
import { supabase } from '@/src/lib/supabase';
import { useOnboarding, type InvitePeek } from '@/src/hooks/useOnboarding';
import TelemetryLogo from '@/src/components/dashboard/logo';
import BootScreen from '@/src/components/boot-screen';
import Shell from '@/src/components/dashboard/shell';
import SignIn from './SignIn';
import VenueDetailsForm from '@/src/components/onboarding/venue-details-form';
import type { OpeningHours } from '@/src/components/onboarding/opening-hours-picker';

function Spinner({ label }: { label: string }) {
  return (
    <div className="fixed inset-0 bg-black flex flex-col items-center justify-center z-[100]">
      <TelemetryLogo className="w-16 h-16" />
      <p className="mt-5 text-[10px] font-bold text-neutral-400 tracking-[0.2em] uppercase">{label}</p>
    </div>
  );
}

export function OnboardingGate({ authUserId, email, userName, onSignOut }: { authUserId: string; email: string; userName: string; onSignOut: () => void }) {
  const [state, setState] = useState<'loading' | 'ready' | 'setup' | 'has-invite' | 'offline'>('loading');

  const check = useCallback(async () => {
    setState((s) => (s === 'offline' ? 'loading' : s));

    const { data, error } = await supabase.from('users').select('id').eq('auth_user_id', authUserId).maybeSingle();
    // A failed request (e.g. no internet) is NOT the same as "no profile".
    // Treating it as "no profile" would send existing users to venue setup.
    if (error) { setState('offline'); return; }
    if (data) { setState('ready'); return; }

    // Check if there's a pending invite for this email
    const { data: invites, error: inviteErr } = await supabase
      .from('invites').select('token').eq('email', email).eq('status', 'pending').limit(1);
    if (inviteErr) { setState('offline'); return; }
    if (invites && invites.length > 0) { setState('has-invite'); return; }

    setState('setup');
  }, [authUserId, email]);

  useEffect(() => { void check(); }, [check]);

  // Retry automatically when the connection comes back.
  useEffect(() => {
    if (state !== 'offline') return;
    const retry = () => { void check(); };
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, [state, check]);

  if (state === 'loading') return <BootScreen />;
  if (state === 'ready') return <Shell />;
  if (state === 'offline') return (
    <div className="fixed inset-0 bg-black text-white flex flex-col items-center justify-center px-6 text-center">
      <TelemetryLogo className="w-14 h-14 mb-6" />
      <h1 className="text-xl font-semibold mb-2">Can't connect</h1>
      <p className="text-sm text-neutral-400 max-w-xs">OpsAlo needs an internet connection. Check your Wi-Fi or mobile data and try again.</p>
      <button onClick={() => void check()} className="mt-8 text-xs font-bold text-leaf-300 uppercase tracking-widest">Try again</button>
    </div>
  );
  if (state === 'has-invite') return (
    <div className="fixed inset-0 bg-black text-white flex flex-col items-center justify-center px-6 text-center">
      <TelemetryLogo className="w-14 h-14 mb-6" />
      <h1 className="text-xl font-semibold mb-2">You have a pending invite</h1>
      <p className="text-sm text-neutral-400 max-w-xs">Check your email for an invite link from your venue manager and use that to join.</p>
      <button onClick={onSignOut} className="mt-8 text-xs font-bold text-leaf-300 uppercase tracking-widest">Sign out</button>
    </div>
  );
  return <FinishSetup email={email} personName={userName} onDone={check} onSignOut={onSignOut} />;
}

function FinishSetup({ email, personName, onDone, onSignOut }: { email: string; personName: string; onDone: () => void; onSignOut: () => void }) {
  const { provision } = useOnboarding();

  async function handleSubmit(data: { name: string; cuisine: string }): Promise<{ error: string | null }> {
    const { error: err, venueId } = await provision(data.name, personName);
    if (err) return { error: err };
    if (venueId && data.cuisine) {
      await supabase.from('venues').update({ cuisine: data.cuisine }).eq('id', venueId);
    }
    onDone();
    return { error: null };
  }

  return (
    <div className="fixed inset-0 bg-black text-white flex flex-col items-center justify-center px-6">
      <VenueDetailsForm
        title="Set up your venue"
        subhead={`Signed in as ${email}. You can fill in the rest anytime from settings.`}
        submitLabel="Create venue"
        onSubmit={handleSubmit}
        onBack={onSignOut}
      />
    </div>
  );
}

// Handles an ?invite=<token> link: peek, auth, accept, then enter the app.
export function InviteFlow({ token, session, signIn, signUp }: {
  token: string;
  session: Session | null;
  signIn: (e: string, p: string) => Promise<{ error: string | null }>;
  signUp: (e: string, p: string, n: string) => Promise<{ error: string | null; needsConfirmation: boolean }>;
}) {
  const { peekInvite, acceptInvite } = useOnboarding();
  const [peek, setPeek] = useState<InvitePeek | null>(null);
  const [peekErr, setPeekErr] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptErr, setAcceptErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const tried = useRef(false);

  useEffect(() => {
    void (async () => {
      const res = await peekInvite(token);
      if ('error' in res) setPeekErr(res.error); else setPeek(res);
    })();
  }, [token, peekInvite]);

  // Once signed in, accept automatically — but only if the session actually
  // matches who this invite is for. A logged-in session for a different
  // email must never silently accept on their behalf.
  const emailMismatch = !!session && !!peek && session.user.email?.toLowerCase() !== peek.email.toLowerCase();

  useEffect(() => {
    // Wait for the invite details first: until they load we can't tell whether the signed-in
    // account is the one invited, and accepting early would try (and fail) as the wrong person.
    if (!session || !peek || done || accepting || tried.current || emailMismatch) return;
    tried.current = true;
    void (async () => {
      setAccepting(true);
      const { error } = await acceptInvite(token);
      setAccepting(false);
      if (error) { setAcceptErr(error); return; }
      setDone(true);
      setTimeout(() => { window.location.href = '/'; }, 900);
    })();
  }, [session, peek, done, accepting, token, acceptInvite, emailMismatch]);

  if (peekErr) {
    return (
      <div className="fixed inset-0 bg-black text-white flex flex-col items-center justify-center px-6 text-center">
        <p className="text-sm text-red-400">{peekErr}</p>
        <a href="/" className="mt-4 text-xs font-bold text-leaf-300 uppercase tracking-widest">Go to sign in</a>
      </div>
    );
  }
  if (!peek) return <Spinner label="Loading invite" />;
  if (done) {
    return (
      <div className="fixed inset-0 bg-black text-white flex flex-col items-center justify-center px-6 text-center">
        <PartyPopper size={40} className="text-leaf-300 mb-4" />
        <h1 className="text-xl font-semibold">You're in</h1>
        <p className="text-sm text-neutral-400 mt-2">Joined {peek.venue_name}. Taking you in…</p>
      </div>
    );
  }
  if (acceptErr) {
    return (
      <div className="fixed inset-0 bg-black text-white flex flex-col items-center justify-center px-6 text-center">
        <p className="text-sm text-red-400 max-w-xs">{acceptErr}</p>
        <a href="/" className="mt-4 text-xs font-bold text-leaf-300 uppercase tracking-widest">Go to app</a>
      </div>
    );
  }

  if (emailMismatch && session) {
    return (
      <div className="fixed inset-0 bg-black text-white flex flex-col items-center justify-center px-6 text-center">
        <h1 className="text-xl font-semibold mb-3">Join {peek.venue_name}</h1>
        <p className="text-sm text-neutral-300 max-w-xs">
          This invite is for <strong className="text-white">{peek.email}</strong>, but you're signed in as{' '}
          <strong className="text-white">{session.user.email}</strong>. Sign out to join with the invited email.
        </p>
        <button
          onClick={async () => { await supabase.auth.signOut(); window.location.reload(); }}
          className="mt-6 text-xs font-bold text-leaf-300 uppercase tracking-widest"
        >
          Sign out and continue
        </button>
        <a href="/" className="mt-4 text-xs font-semibold text-neutral-400">Stay signed in as {session.user.email}</a>
      </div>
    );
  }

  if (session) return <Spinner label="Joining venue" />;

  // Not signed in: show who invited them, then auth (defaults to create-account).
  const roleLabel = peek.role.charAt(0).toUpperCase() + peek.role.slice(1);
  return (
    <SignIn
      onSignIn={signIn}
      onSignUp={signUp}
      lockedEmail={peek.email}
      startMode={peek.userExists ? 'signin' : 'signup'}
      headline={`Join ${peek.venue_name}`}
      subhead={`Invited as ${roleLabel}`}
      skipInviteCheck
    />
  );
}
